import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { ArrowRight, CheckCircle2, Clock, Layers } from 'lucide-react'
import Footer from '../components/Footer'

interface ProjectItem {
  slug: string
  name: string
  tagline: string
  difficulty: string
  estimatedTime: string
  features: string[]
  techStack: string[]
}

const projects: ProjectItem[] = [
  {
    slug: 'devcollab',
    name: 'DevCollab',
    tagline: 'Real-time collaborative developer knowledge base with markdown co-editing, file uploads, and realtime voting.',
    difficulty: 'Intermediate',
    estimatedTime: '4–6 hours',
    features: ['Auth', 'Realtime', 'File Storage'],
    techStack: ['React', 'TypeScript', 'Solarch SDK', 'Tailwind CSS'],
  },
  {
    slug: 'neuralhire',
    name: 'NeuralHire',
    tagline: 'AI-powered recruitment platform leveraging resume embeddings, vector search similarity, and automated candidate workflows.',
    difficulty: 'Advanced',
    estimatedTime: '8–12 hours',
    features: ['Auth', 'Realtime', 'Migrations'],
    techStack: ['Next.js', 'TypeScript', 'Solarch SDK', 'OpenAI API', 'Tailwind CSS'],
  },
  {
    slug: 'pixelvault',
    name: 'PixelVault',
    tagline: 'Multiplayer game asset marketplace with realtime bidding auctions, 3D asset previews, and protected S3 file storage.',
    difficulty: 'Advanced',
    estimatedTime: '10–14 hours',
    features: ['Auth', 'Realtime', 'File Storage'],
    techStack: ['React', 'TypeScript', 'Solarch SDK', 'Three.js', 'Tailwind CSS'],
  },
]

export default function Projects() {
  return (
    <>
      <SEO 
        title="Solarch Reference Projects & Architecture Guides"
        description="Explore production reference projects built with Solarch: realtime collaborative workspaces, AI vector search pipelines, and S3 digital asset marketplaces."
        canonical="/projects"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Solarch Reference Projects & Architecture Guides",
          "url": "https://solarch.in/projects",
          "description": "Explore production reference projects built with Solarch: realtime collaborative workspaces, AI vector search pipelines, and S3 digital asset marketplaces.",
          "about": {
            "@id": "https://solarch.in/#software"
          },
          "publisher": {
            "@id": "https://solarch.in/#organization"
          }
        }}
      />

      <div className="min-h-screen bg-theme-body pt-14 flex flex-col">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
            <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-primary blur-3xl" />
            <div className="absolute -left-20 top-40 h-64 w-64 rounded-full bg-accent blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24 text-center">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="font-heading text-4xl font-bold tracking-tight text-theme sm:text-5xl lg:text-6xl">
                Built with <span className="text-primary">Solarch</span>
              </h1>
              <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-theme-secondary leading-relaxed">
                Step-by-step architectural guides demonstrating real-world applications powered by Solarch authentication, realtime subscriptions, file storage, and vector search.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Project Guides Grid */}
        <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {projects.map((project, idx) => (
              <motion.div
                key={project.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="group relative flex flex-col justify-between rounded-2xl border border-[#3a2214] bg-[#150d08] p-6 sm:p-7 shadow-xl hover:border-primary/50 transition-all duration-300"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/25">
                      {project.difficulty}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-theme-muted font-mono">
                      <Clock size={12} />
                      <span>{project.estimatedTime}</span>
                    </div>
                  </div>

                  <h2 className="font-heading text-xl font-bold text-theme mb-2 group-hover:text-primary transition-colors">
                    {project.name}
                  </h2>

                  <p className="text-sm text-theme-secondary leading-relaxed mb-6">
                    {project.tagline}
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="text-xs font-semibold text-theme-muted uppercase tracking-wider flex items-center gap-1.5">
                      <Layers size={13} className="text-primary" />
                      <span>Features Tested</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {project.features.map((feature) => (
                        <span
                          key={feature}
                          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-[#0a0603] text-[#fdf3ec] border border-[#3a2214]"
                        >
                          <CheckCircle2 size={10} className="text-primary" />
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#3a2214]/60">
                  <Link
                    to={`/projects/${project.slug}`}
                    className="inline-flex items-center justify-between w-full text-sm font-semibold text-primary hover:text-white transition-colors"
                  >
                    <span>Read Architectural Guide</span>
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
